import { useState, useEffect, useCallback } from "react";
import pharmacySearchService from "../api/pharmacySearchService";

/**
 * Custom hook for pharmacy search functionality
 * Provides state management and methods for searching pharmacy components
 */
export const usePharmacySearch = () => {
  const [searchResults, setSearchResults] = useState({
    pos_orders: [],
    prescriptions: [],
    free_medicine_orders: [],
    total_count: 0,
    page: 1,
    page_size: 10,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  /**
   * Perform search with given parameters
   */
  const search = useCallback(async (searchParams = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await pharmacySearchService.searchPharmacy(searchParams);
      setSearchResults(results);
      return results;
    } catch (err) {
      setError(err.message || "Search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Quick search for suggestions
   */
  const quickSearch = useCallback(async (query, limit = 10) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return [];
    }

    try {
      const result = await pharmacySearchService.quickSearch(query, limit);
      setSuggestions(result.suggestions || []);
      return result.suggestions || [];
    } catch (err) {
      console.error("Quick search error:", err);
      setSuggestions([]);
      return [];
    }
  }, []);

  /**
   * Search POS orders specifically
   */
  const searchPOSOrders = useCallback(async (searchParams = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await pharmacySearchService.searchPOSOrders(searchParams);
      setSearchResults((prev) => ({
        ...prev,
        pos_orders: results.pos_orders || [],
        pos_count: results.pos_count || 0,
      }));
      return results;
    } catch (err) {
      setError(err.message || "POS search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Search prescriptions specifically
   */
  const searchPrescriptions = useCallback(async (searchParams = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await pharmacySearchService.searchPrescriptions(
        searchParams
      );
      setSearchResults((prev) => ({
        ...prev,
        prescriptions: results.prescriptions || [],
        prescription_count: results.prescription_count || 0,
      }));
      return results;
    } catch (err) {
      setError(err.message || "Prescription search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Search free medicine orders specifically
   */
  const searchFreeMedicineOrders = useCallback(async (searchParams = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await pharmacySearchService.searchFreeMedicineOrders(
        searchParams
      );
      setSearchResults((prev) => ({
        ...prev,
        free_medicine_orders: results.free_medicine_orders || [],
        free_medicine_count: results.free_medicine_count || 0,
      }));
      return results;
    } catch (err) {
      setError(err.message || "Free medicine search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Search with filters
   */
  const searchWithFilters = useCallback(
    async (filters = {}, pagination = {}) => {
      setIsLoading(true);
      setError(null);

      try {
        const results = await pharmacySearchService.searchWithFilters(
          filters,
          pagination
        );
        setSearchResults(results);
        return results;
      } catch (err) {
        setError(err.message || "Filtered search failed");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Search by date range
   */
  const searchByDateRange = useCallback(
    async (dateFrom, dateTo, type = "all") => {
      setIsLoading(true);
      setError(null);

      try {
        const results = await pharmacySearchService.searchByDateRange(
          dateFrom,
          dateTo,
          type
        );
        setSearchResults(results);
        return results;
      } catch (err) {
        setError(err.message || "Date range search failed");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Search by payment status
   */
  const searchByPaymentStatus = useCallback(
    async (paymentStatus, type = "all") => {
      setIsLoading(true);
      setError(null);

      try {
        const results = await pharmacySearchService.searchByPaymentStatus(
          paymentStatus,
          type
        );
        setSearchResults(results);
        return results;
      } catch (err) {
        setError(err.message || "Payment status search failed");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Search by patient
   */
  const searchByPatient = useCallback(async (patientId, type = "all") => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await pharmacySearchService.searchByPatient(
        patientId,
        type
      );
      setSearchResults(results);
      return results;
    } catch (err) {
      setError(err.message || "Patient search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Search by doctor
   */
  const searchByDoctor = useCallback(async (doctorId) => {
    setIsLoading(true);
    setError(null);

    try {
      const results = await pharmacySearchService.searchByDoctor(doctorId);
      setSearchResults(results);
      return results;
    } catch (err) {
      setError(err.message || "Doctor search failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get suggestions by type
   */
  const getSuggestionsByType = useCallback(async (type, query) => {
    try {
      const suggestions = await pharmacySearchService.getSuggestionsByType(
        type,
        query
      );
      return suggestions;
    } catch (err) {
      console.error("Get suggestions error:", err);
      return [];
    }
  }, []);

  /**
   * Clear search results
   */
  const clearResults = useCallback(() => {
    setSearchResults({
      pos_orders: [],
      prescriptions: [],
      free_medicine_orders: [],
      total_count: 0,
      page: 1,
      page_size: 10,
    });
    setSuggestions([]);
    setError(null);
  }, []);

  /**
   * Clear suggestions
   */
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    // State
    searchResults,
    isLoading,
    error,
    suggestions,

    // Methods
    search,
    quickSearch,
    searchPOSOrders,
    searchPrescriptions,
    searchFreeMedicineOrders,
    searchWithFilters,
    searchByDateRange,
    searchByPaymentStatus,
    searchByPatient,
    searchByDoctor,
    getSuggestionsByType,
    clearResults,
    clearSuggestions,
  };
};

/**
 * Hook for debounced search
 */
export const useDebouncedPharmacySearch = (delay = 300) => {
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [query, setQuery] = useState("");

  const {
    searchResults,
    isLoading,
    error,
    suggestions,
    search,
    quickSearch,
    clearResults,
    clearSuggestions,
  } = usePharmacySearch();

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, delay);

    return () => clearTimeout(timer);
  }, [query, delay]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery) {
      search({ search: debouncedQuery });
    } else {
      clearResults();
    }
  }, [debouncedQuery, search, clearResults]);

  // Perform quick search for suggestions
  useEffect(() => {
    if (query && query.length >= 2) {
      quickSearch(query);
    } else {
      clearSuggestions();
    }
  }, [query, quickSearch, clearSuggestions]);

  return {
    query,
    setQuery,
    debouncedQuery,
    searchResults,
    isLoading,
    error,
    suggestions,
    search,
    quickSearch,
    clearResults,
    clearSuggestions,
  };
};

export default usePharmacySearch;
